import React, { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { fmtDateTime } from "@/lib/format";
import {
  Plus,
  Trash2,
  CheckCircle2,
  AlertCircle,
  Loader2,
  Sparkles,
  Send,
  Pencil,
  Eye,
  X,
  CalendarClock,
  EyeOff,
} from "lucide-react";
import ReactMarkdown from "react-markdown";
import DropZone from "@/components/DropZone";
import ConfirmDialog from "@/components/ConfirmDialog";
import CategoryManager from "@/components/CategoryManager";
import PromptEditor from "@/components/PromptEditor";
import PublishTargetModal from "@/components/PublishTargetModal";
import { useAuth } from "@/lib/auth";
import { runWithConcurrency } from "@/lib/concurrency";

const UPLOAD_CONCURRENCY = 3;

const StatusIcon = ({ state }) => {
  if (state === "done") return <CheckCircle2 size={14} className="text-emerald-600" />;
  if (state === "error") return <AlertCircle size={14} className="text-red-500" />;
  if (state === "uploading") return <Loader2 size={14} className="text-blue-600 animate-spin" />;
  return <div className="w-2 h-2 rounded-full bg-slate-300" />;
};

const monthOptions = () => {
  const opts = [];
  const now = new Date();
  for (let i = 0; i < 24; i++) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, "0");
    const label = d.toLocaleDateString("it-IT", { month: "long", year: "numeric" });
    opts.push({ value: `${y}-${m}`, label: label[0].toUpperCase() + label.slice(1) });
  }
  return opts;
};

const StatusBadge = ({ r }) => {
  if (r.generation_status === "generating") {
    return (
      <span className="badge-neutral !bg-purple-50 !text-purple-700 font-semibold flex items-center gap-1 text-[10px]">
        <Loader2 size={10} className="animate-spin" /> GENERAZIONE
      </span>
    );
  }
  if (r.generation_status === "error") {
    return <span className="badge-neutral !bg-red-50 !text-red-700 font-semibold text-[10px]">ERRORE</span>;
  }
  if (r.status === "draft") {
    return <span className="badge-neutral !bg-amber-50 !text-amber-700 font-semibold text-[10px]">BOZZA</span>;
  }
  return <span className="badge-pos flex items-center gap-1 text-[10px]"><CheckCircle2 size={10} /> Pubblicato</span>;
};

export default function AdminReports() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [items, setItems] = useState([]);
  const [users, setUsers] = useState([]);
  // Upload multi
  const [form, setForm] = useState({ description: "", category: "Mensile", status: "published", visibility: "all" });
  const [files, setFiles] = useState([]);
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState({});
  // Monthly summary
  const [monthlyMonth, setMonthlyMonth] = useState(monthOptions()[1].value); // last full month
  const [monthlyTitle, setMonthlyTitle] = useState("");
  const [monthlyCategory, setMonthlyCategory] = useState("");
  const [monthlyVisibility, setMonthlyVisibility] = useState("all");
  const [monthlyTargets, setMonthlyTargets] = useState([]);
  const [monthlyBusy, setMonthlyBusy] = useState(false);
  // Edit modal
  const [editing, setEditing] = useState(null);
  const [editForm, setEditForm] = useState({
    title: "",
    description: "",
    category: "",
    visibility: "all",
    target_client_ids: [],
    content_markdown: "",
  });
  const [previewMode, setPreviewMode] = useState(false);
  const [savingEdit, setSavingEdit] = useState(false);
  // Confirmation dialog (replaces window.confirm which Chrome can silently disable)
  const [confirmState, setConfirmState] = useState(null);
  const [publishModal, setPublishModal] = useState(null); // { item }
  // Categories (used for datalist combobox)
  const [categories, setCategories] = useState([]);
  const loadCategories = async () => {
    try {
      const { data } = await api.get("/reports/categories");
      setCategories(data);
    } catch (_e) { /* silent */ }
  };

  const load = async () => {
    const { data } = await api.get("/reports");
    setItems(data);
    return (data || []).some((r) => r.generation_status === "generating");
  };
  const loadUsers = async () => {
    try {
      const { data } = await api.get("/users");
      setUsers(data.filter((u) => u.role === "cliente"));
    } catch (_e) { /* silent */ }
  };

  useEffect(() => {
    load();
    loadUsers();
    loadCategories();
  }, []);

  // Poll while any report is generating
  useEffect(() => {
    const hasGenerating = items.some((r) => r.generation_status === "generating");
    if (!hasGenerating) return;
    const t = setInterval(() => { load(); }, 5000);
    return () => clearInterval(t);
  }, [items]);

  // --- Multi upload ---
  const uploadOne = async (file) => {
    setProgress((p) => ({ ...p, [file.name]: "uploading" }));
    const fd = new FormData();
    fd.append("file", file);
    const { data: up } = await api.post("/reports/upload-pdf", fd, {
      headers: { "Content-Type": "multipart/form-data" },
    });
    const title = file.name.replace(/\.pdf$/i, "");
    await api.post("/reports", { ...form, title, storage_path: up.storage_path, filename: up.filename });
    setProgress((p) => ({ ...p, [file.name]: "done" }));
  };

  const create = async () => {
    if (!files.length) return toast.error("Seleziona almeno un PDF");
    setBusy(true);
    setProgress(Object.fromEntries(files.map((f) => [f.name, "pending"])));
    try {
      const results = await runWithConcurrency(files, UPLOAD_CONCURRENCY, async (f) => {
        try {
          await uploadOne(f);
          return { ok: true, name: f.name };
        } catch (e) {
          setProgress((p) => ({ ...p, [f.name]: "error" }));
          return { ok: false, name: f.name };
        }
      });
      const ok = results.filter((r) => r?.ok).length;
      const ko = results.length - ok;
      if (ok) toast.success(`${ok} report pubblicato/i`);
      if (ko) toast.error(`${ko} upload falliti`);
      const okNames = new Set(results.filter((r) => r?.ok).map((r) => r.name));
      setFiles((prev) => prev.filter((f) => !okNames.has(f.name)));
      load();
      loadCategories();
    } finally {
      setBusy(false);
    }
  };

  // --- Monthly summary generation ---
  const toggleMonthlyTarget = (id) => {
    setMonthlyTargets((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  };

  const generateMonthly = async () => {
    setMonthlyBusy(true);
    try {
      await api.post("/reports/monthly-summary/generate", {
        month: monthlyMonth,
        title: monthlyTitle || undefined,
        category: monthlyCategory || undefined,
        visibility: monthlyVisibility,
        target_client_ids: monthlyVisibility === "specific" ? monthlyTargets : [],
      });
      toast.success("Generazione avviata — richiede 2-4 minuti. Il riepilogo comparirà in bozza qui sotto.");
      setMonthlyTitle("");
      setMonthlyCategory("");
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore avvio generazione");
    } finally {
      setMonthlyBusy(false);
    }
  };

  // --- Edit modal ---
  const openEdit = (r) => {
    setEditing(r);
    setEditForm({
      title: r.title || "",
      description: r.description || "",
      category: r.category || "",
      visibility: r.visibility || "all",
      target_client_ids: r.target_client_ids || [],
      content_markdown: r.content_markdown || "",
    });
    setPreviewMode(false);
  };
  const closeEdit = () => setEditing(null);
  const toggleEditTarget = (id) => {
    setEditForm((prev) => ({
      ...prev,
      target_client_ids: prev.target_client_ids.includes(id)
        ? prev.target_client_ids.filter((x) => x !== id)
        : [...prev.target_client_ids, id],
    }));
  };
  const saveEdit = async () => {
    if (!editing) return;
    if (!editForm.title.trim()) return toast.error("Titolo obbligatorio");
    setSavingEdit(true);
    try {
      await api.patch(`/reports/${editing.id}`, {
        title: editForm.title.trim(),
        description: editForm.description,
        category: editForm.category,
        visibility: editForm.visibility,
        target_client_ids: editForm.visibility === "specific" ? editForm.target_client_ids : [],
        content_markdown: editing.report_kind === "monthly_summary" ? editForm.content_markdown : undefined,
      });
      toast.success("Modifiche salvate");
      closeEdit();
      load();
      loadCategories();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore salvataggio");
    } finally {
      setSavingEdit(false);
    }
  };

  const publish = (r) => {
    setPublishModal({
      item: r,
      onConfirm: async ({ target, company_ids, client_ids, send_notification, send_email, email_subject, email_message, email_client_ids, email_company_ids, email_group_id }) => {
        try {
          await api.post(`/reports/${r.id}/publish`, {
            send_notification,
            send_email,
            email_subject,
            email_message,
            visibility: target === "companies" ? "companies" : target === "users" ? "specific" : "all",
            target_company_ids: target === "companies" ? company_ids : [],
            target_client_ids: target === "users" ? client_ids : [],
            email_client_ids: email_client_ids || [],
            email_company_ids: email_company_ids || [],
            email_group_id: email_group_id || null,
          });
          toast.success(
            "Report pubblicato" +
              (target === "companies"
                ? ` a ${company_ids.length} aziend${company_ids.length === 1 ? "a" : "e"}`
                : target === "users"
                ? ` a ${client_ids.length} utent${client_ids.length === 1 ? "e" : "i"}`
                : " a tutti i clienti")
          );
          setPublishModal(null);
          load();
        } catch (e) {
          toast.error(e.response?.data?.detail || "Errore pubblicazione");
        }
      },
    });
  };

  const del = (r) => {
    setConfirmState({
      title: "Eliminare questo report?",
      message: `"${r.title}" verrà rimosso definitivamente.`,
      confirmLabel: "Elimina",
      variant: "danger",
      onConfirm: async () => {
        try {
          await api.delete(`/reports/${r.id}`);
          toast.success("Report eliminato");
          load();
        } catch (e) {
          toast.error(e.response?.data?.detail || "Errore eliminazione");
        }
      },
    });
  };

  const monthlyReports = useMemo(
    () => items.filter((r) => r.report_kind === "monthly_summary"),
    [items],
  );
  const otherReports = useMemo(
    () => items.filter((r) => r.report_kind !== "monthly_summary"),
    [items],
  );

  return (
    <div className="space-y-6">
      <div>
        <h1 className="font-display text-3xl font-bold tracking-tight text-slate-900">Gestione Analisi e altri Report</h1>
        <p className="text-sm text-slate-500 mt-1">
          Carica PDF esterni o genera un riepilogo mensile AI. La pubblicazione rende il documento visibile nella sezione &quot;Analisi mensile e altri report&quot; dei clienti.
        </p>
      </div>

      {/* Categorie management */}
      <CategoryManager
        endpoint="/reports/categories"
        title="Categorie report"
        subtitle="Le nuove categorie diventano subito filtri per i clienti nella pagina Report."
        confirmDelete={setConfirmState}
        onChange={loadCategories}
      />

      {/* Section 1: Monthly summary generator */}
      <div className="card-elevated p-6" data-testid="monthly-summary-card">
        <div className="flex items-center gap-4 flex-wrap">
          <div className="w-14 h-14 rounded-2xl brand-gradient flex items-center justify-center shadow-lg shadow-purple-500/40 flex-shrink-0">
            <CalendarClock className="text-white" size={26} />
          </div>
          <div className="flex-1 min-w-[220px]">
            <div className="font-display font-bold text-lg text-slate-900">Riepilogo mensile AI</div>
            <div className="text-sm text-slate-500">
              Genera una bozza (dati + sintesi analitica di Claude Sonnet). Potrai modificarla e poi pubblicarla ai clienti target.
            </div>
          </div>
          <button
            onClick={generateMonthly}
            disabled={monthlyBusy}
            className="btn-brand flex items-center gap-2"
            data-testid="monthly-generate-btn"
          >
            {monthlyBusy ? <Loader2 size={16} className="animate-spin" /> : <Sparkles size={16} />}
            {monthlyBusy ? "Avvio…" : "Genera bozza"}
          </button>
        </div>
        <div className="mt-4 pt-4 border-t border-slate-100 grid grid-cols-1 md:grid-cols-2 gap-3">
          <div>
            <label className="text-[11px] text-slate-500 uppercase tracking-wide font-semibold">Mese di riferimento</label>
            <select
              value={monthlyMonth}
              onChange={(e) => setMonthlyMonth(e.target.value)}
              className="mt-1 w-full px-3 py-2.5 rounded-lg border border-slate-200 text-sm bg-white outline-none focus:border-blue-500"
              data-testid="monthly-month"
            >
              {monthOptions().map((m) => (
                <option key={m.value} value={m.value}>{m.label}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="text-[11px] text-slate-500 uppercase tracking-wide font-semibold">Titolo (opzionale)</label>
            <input
              value={monthlyTitle}
              onChange={(e) => setMonthlyTitle(e.target.value)}
              placeholder="Es. Riepilogo Gennaio 2026 — Mercati Energia"
              className="mt-1 w-full px-3 py-2.5 rounded-lg border border-slate-200 text-sm outline-none focus:border-blue-500"
              data-testid="monthly-title"
            />
          </div>
        </div>
        <div className="mt-3">
          <label className="text-[11px] text-slate-500 uppercase tracking-wide font-semibold">Categoria (default: &quot;Riepilogo Mensile&quot;)</label>
          <input
            list="monthly-cat-list"
            value={monthlyCategory}
            onChange={(e) => setMonthlyCategory(e.target.value)}
            placeholder="Riepilogo Mensile"
            className="mt-1 w-full px-3 py-2.5 rounded-lg border border-slate-200 text-sm outline-none focus:border-blue-500"
            data-testid="monthly-category"
          />
          <datalist id="monthly-cat-list">
            {categories.map((c) => <option key={c.name} value={c.name} />)}
          </datalist>
        </div>
        <div className="mt-3">
          <label className="text-[11px] text-slate-500 uppercase tracking-wide font-semibold block mb-2">Visibilità (modificabile in seguito)</label>
          <div className="flex flex-wrap gap-3 text-sm">
            <label className="flex items-center gap-2 cursor-pointer" data-testid="monthly-vis-all">
              <input type="radio" name="monthly-vis" checked={monthlyVisibility === "all"} onChange={() => setMonthlyVisibility("all")} />
              <span>Tutti i clienti</span>
            </label>
            <label className="flex items-center gap-2 cursor-pointer" data-testid="monthly-vis-specific">
              <input type="radio" name="monthly-vis" checked={monthlyVisibility === "specific"} onChange={() => setMonthlyVisibility("specific")} />
              <span>Solo clienti selezionati</span>
            </label>
          </div>
          {monthlyVisibility === "specific" && (
            <div className="mt-2 border border-slate-200 rounded-xl p-3 max-h-48 overflow-y-auto bg-slate-50/40">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-1">
                {users.map((u) => (
                  <label key={u.id} className="flex items-center gap-2 text-sm py-1 px-2 rounded hover:bg-white cursor-pointer">
                    <input type="checkbox" checked={monthlyTargets.includes(u.id)} onChange={() => toggleMonthlyTarget(u.id)} />
                    <span className="truncate">
                      <b>{u.company_name || u.full_name}</b> <span className="text-slate-400">· {u.email}</span>
                    </span>
                  </label>
                ))}
              </div>
              <div className="mt-1 text-xs text-slate-500">{monthlyTargets.length} selezionati</div>
            </div>
          )}
        </div>
      </div>

      {/* Section 2: Multi upload */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div>
          <h2 className="font-display text-2xl font-bold tracking-tight text-slate-900 mb-3">Upload PDF esterni</h2>
          <div className="card-elevated p-5 space-y-3">
            <input placeholder="Descrizione breve (applicata a tutti)" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="w-full px-4 py-2.5 rounded-lg border border-slate-200 text-sm outline-none focus:border-blue-500" />
            <div className="flex gap-2">
              <input list="report-cat-list" placeholder="Categoria" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} className="flex-1 px-4 py-2.5 rounded-lg border border-slate-200 text-sm outline-none focus:border-blue-500" data-testid="report-category-input" />
              <datalist id="report-cat-list">
                {categories.map((c) => <option key={c.name} value={c.name} />)}
              </datalist>
              <select value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })} className="px-4 py-2.5 rounded-lg border border-slate-200 text-sm bg-white outline-none focus:border-blue-500">
                <option value="published">Pubblicato</option>
                <option value="draft">Bozza</option>
              </select>
            </div>
            <DropZone
              files={files}
              multiple
              onChange={setFiles}
              accept=".pdf,application/pdf"
              hint="Trascina qui i PDF oppure clicca per selezionarli"
              testId="report-pdf-drop"
            />
            <p className="text-xs text-slate-500">
              Il titolo di ogni report sarà il nome del file (senza .pdf). Upload paralleli: max {UPLOAD_CONCURRENCY} contemporanei.
            </p>
            {busy && files.length > 0 && (
              <div className="space-y-1 pt-1" data-testid="report-upload-progress">
                {files.map((f) => (
                  <div key={f.name} className="flex items-center gap-2 text-xs text-slate-600">
                    <StatusIcon state={progress[f.name] || "pending"} />
                    <span className="truncate">{f.name}</span>
                  </div>
                ))}
              </div>
            )}
            <button onClick={create} disabled={busy || !files.length} className="btn-brand flex items-center gap-2 disabled:opacity-40" data-testid="report-create">
              <Plus size={16} /> {busy ? "Caricamento…" : `Pubblica ${files.length || ""} report`}
            </button>
          </div>
        </div>

        {/* Prompt editor visibile solo agli admin */}
        {isAdmin && <PromptEditor promptKey="monthly_summary" />}

        {/* Section 3: Monthly summary drafts */}
        <div>
          <h2 className="font-display text-2xl font-bold tracking-tight text-slate-900 mb-3">Riepiloghi mensili</h2>
          <div className="space-y-2" data-testid="monthly-summary-list">
            {monthlyReports.length === 0 && (
              <div className="text-sm text-slate-400">Nessun riepilogo generato.</div>
            )}
            {monthlyReports.map((r) => (
              <div key={r.id} className="card-elevated p-4" data-testid={`monthly-row-${r.id}`}>
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <StatusBadge r={r} />
                      <span className="text-xs text-slate-500">{r.report_month || fmtDateTime(r.report_date)}</span>
                    </div>
                    <div className="font-semibold text-slate-900 text-sm mt-1 truncate">{r.title}</div>
                    <div className="text-xs text-slate-500 truncate">
                      {r.visibility === "specific"
                        ? `${(r.target_client_ids || []).length} clienti selezionati`
                        : "Visibile a tutti"}
                    </div>
                  </div>
                  <div className="flex items-center gap-1 flex-wrap">
                    {r.generation_status === "generating" ? (
                      <span className="text-xs text-purple-600 flex items-center gap-1">
                        <Loader2 size={12} className="animate-spin" /> generazione…
                      </span>
                    ) : r.generation_status === "error" ? (
                      <span className="text-xs text-red-600" title={r.error_message}>errore</span>
                    ) : (
                      <>
                        <button onClick={() => openEdit(r)} className="p-1.5 text-slate-500 hover:text-blue-600" title="Modifica" data-testid={`monthly-edit-${r.id}`}>
                          <Pencil size={15} />
                        </button>
                        {r.status === "draft" && (
                          <button onClick={() => publish(r)} className="p-1.5 text-emerald-600 hover:text-emerald-700" title="Pubblica" data-testid={`monthly-publish-${r.id}`}>
                            <Send size={15} />
                          </button>
                        )}
                      </>
                    )}
                    <button onClick={() => del(r)} className="p-1.5 text-red-500 hover:text-red-700" title="Elimina" data-testid={`monthly-delete-${r.id}`}>
                      <Trash2 size={15} />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Section 4: All uploaded reports (existing list) */}
      <div>
        <h2 className="font-display text-2xl font-bold tracking-tight text-slate-900 mb-3">Report caricati</h2>
        <div className="space-y-2">
          {otherReports.map((r) => (
            <div key={r.id} className="card-elevated p-4 flex items-center justify-between" data-testid={`report-row-${r.id}`}>
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2 flex-wrap">
                  <StatusBadge r={r} />
                  <span className="text-xs text-slate-500">{r.category} · {fmtDateTime(r.report_date)}</span>
                </div>
                <div className="font-semibold text-slate-900 text-sm mt-1 truncate">{r.title}</div>
              </div>
              <div className="flex items-center gap-1">
                <button onClick={() => openEdit(r)} className="p-1.5 text-slate-500 hover:text-blue-600" title="Modifica" data-testid={`report-edit-${r.id}`}>
                  <Pencil size={15} />
                </button>
                {r.status === "draft" && (
                  <button onClick={() => publish(r)} className="p-1.5 text-emerald-600 hover:text-emerald-700" title="Pubblica" data-testid={`report-publish-${r.id}`}>
                    <Send size={15} />
                  </button>
                )}
                <button onClick={() => del(r)} className="p-1.5 text-red-500 hover:text-red-700" data-testid={`report-delete-${r.id}`}>
                  <Trash2 size={15} />
                </button>
              </div>
            </div>
          ))}
          {otherReports.length === 0 && <div className="text-sm text-slate-400">Nessun report caricato</div>}
        </div>
      </div>

      {/* Edit modal */}
      {editing && (
        <div className="fixed inset-0 z-50 bg-slate-900/40 backdrop-blur-sm flex items-center justify-center p-4" onClick={closeEdit} data-testid="report-edit-modal">
          <div className="bg-white rounded-xl shadow-xl max-w-3xl w-full max-h-[90vh] overflow-y-auto p-6" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-start justify-between mb-4">
              <div>
                <h3 className="font-display text-xl font-semibold text-slate-900">
                  {editing.report_kind === "monthly_summary" ? "Modifica riepilogo mensile" : "Modifica report"}
                </h3>
                <p className="text-xs text-slate-500 mt-0.5">
                  {editing.filename || "—"}
                  {editing.report_kind === "monthly_summary" && " · Pubblicando verrà rigenerato il PDF dal testo qui sotto."}
                </p>
              </div>
              <button onClick={closeEdit} className="text-slate-400 hover:text-slate-600 p-1" data-testid="report-edit-close">
                <X size={18} />
              </button>
            </div>
            <div className="space-y-3">
              <div>
                <label className="text-[11px] text-slate-500 uppercase tracking-wide font-semibold">Titolo</label>
                <input value={editForm.title} onChange={(e) => setEditForm({ ...editForm, title: e.target.value })} className="mt-1 w-full px-3 py-2.5 rounded-lg border border-slate-200 text-sm outline-none focus:border-blue-500" data-testid="report-edit-title" />
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div>
                  <label className="text-[11px] text-slate-500 uppercase tracking-wide font-semibold">Descrizione</label>
                  <input value={editForm.description} onChange={(e) => setEditForm({ ...editForm, description: e.target.value })} className="mt-1 w-full px-3 py-2.5 rounded-lg border border-slate-200 text-sm outline-none focus:border-blue-500" data-testid="report-edit-description" />
                </div>
                <div>
                  <label className="text-[11px] text-slate-500 uppercase tracking-wide font-semibold">Categoria</label>
                  <input list="report-cat-list-edit" value={editForm.category} onChange={(e) => setEditForm({ ...editForm, category: e.target.value })} className="mt-1 w-full px-3 py-2.5 rounded-lg border border-slate-200 text-sm outline-none focus:border-blue-500" data-testid="report-edit-category" />
                  <datalist id="report-cat-list-edit">
                    {categories.map((c) => <option key={c.name} value={c.name} />)}
                  </datalist>
                </div>
              </div>
              <div>
                <label className="text-[11px] text-slate-500 uppercase tracking-wide font-semibold block mb-2">Visibilità</label>
                <div className="flex flex-wrap gap-3 text-sm">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input type="radio" name="edit-vis" checked={editForm.visibility === "all"} onChange={() => setEditForm({ ...editForm, visibility: "all" })} />
                    <span>Tutti i clienti</span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input type="radio" name="edit-vis" checked={editForm.visibility === "specific"} onChange={() => setEditForm({ ...editForm, visibility: "specific" })} />
                    <span>Solo clienti selezionati</span>
                  </label>
                </div>
                {editForm.visibility === "specific" && (
                  <div className="mt-2 border border-slate-200 rounded-xl p-3 max-h-40 overflow-y-auto bg-slate-50/40">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-1">
                      {users.map((u) => (
                        <label key={u.id} className="flex items-center gap-2 text-sm py-1 px-2 rounded hover:bg-white cursor-pointer">
                          <input type="checkbox" checked={editForm.target_client_ids.includes(u.id)} onChange={() => toggleEditTarget(u.id)} />
                          <span className="truncate">
                            <b>{u.company_name || u.full_name}</b> <span className="text-slate-400">· {u.email}</span>
                          </span>
                        </label>
                      ))}
                    </div>
                    <div className="mt-1 text-xs text-slate-500">{editForm.target_client_ids.length} selezionati</div>
                  </div>
                )}
              </div>
              {editing.report_kind === "monthly_summary" && (
                <div>
                  <div className="flex items-center justify-between mb-1">
                    <label className="text-[11px] text-slate-500 uppercase tracking-wide font-semibold">Testo (Markdown)</label>
                    <button
                      type="button"
                      onClick={() => setPreviewMode(!previewMode)}
                      className="text-xs px-2 py-1 rounded-lg border border-slate-200 hover:border-blue-500 flex items-center gap-1"
                    >
                      {previewMode ? <><Pencil size={12} /> Modifica</> : <><Eye size={12} /> Anteprima</>}
                    </button>
                  </div>
                  {previewMode ? (
                    <div className="prose prose-slate prose-sm max-w-none border border-slate-200 rounded-lg p-3 bg-slate-50/50 min-h-[240px] max-h-[400px] overflow-y-auto">
                      <ReactMarkdown>{editForm.content_markdown}</ReactMarkdown>
                    </div>
                  ) : (
                    <textarea
                      value={editForm.content_markdown}
                      onChange={(e) => setEditForm({ ...editForm, content_markdown: e.target.value })}
                      rows={14}
                      className="w-full px-3 py-2.5 rounded-lg border border-slate-200 text-sm font-mono outline-none focus:border-blue-500 leading-relaxed"
                      data-testid="report-edit-content"
                    />
                  )}
                </div>
              )}
            </div>
            <div className="flex justify-end gap-2 mt-5">
              <button onClick={closeEdit} className="px-4 py-2 rounded-lg text-sm text-slate-600 hover:bg-slate-100">Annulla</button>
              <button onClick={saveEdit} disabled={savingEdit} className="btn-brand disabled:opacity-40" data-testid="report-edit-save">
                {savingEdit ? "Salvataggio…" : "Salva modifiche"}
              </button>
            </div>
          </div>
        </div>
      )}

      <ConfirmDialog state={confirmState} onClose={() => setConfirmState(null)} />
      <PublishTargetModal
        open={!!publishModal}
        title="Pubblica report"
        message={
          publishModal?.item
            ? `"${publishModal.item.title}" — scegli il pubblico destinatario.${publishModal.item.report_kind === "monthly_summary" ? " Il PDF verrà rigenerato dal testo corrente." : ""}`
            : undefined
        }
        defaultEmailSubject={publishModal?.item ? `Nuovo report: ${publishModal.item.title}` : ""}
        defaultEmailMessage={publishModal?.item ? (publishModal.item.description || publishModal.item.title) : ""}
        onCancel={() => setPublishModal(null)}
        onConfirm={publishModal?.onConfirm || (() => {})}
      />
    </div>
  );
}
